import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData, patchData } from '../../services/request'

const InfoRow = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-3 text-sm">
    <div className="col-span-1 font-medium text-gray-600">{label}</div>
    <div className="col-span-2 text-gray-900">{value || '—'}</div>
  </div>
)

const AdminCompanyDetails = () => {
  const { companyId } = useParams()
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actioning, setActioning] = useState(false)

  const fetchCompany = async () => {
    setLoading(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/companies/${companyId}`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error === '') {
      if (res.result?.status === 200) {
        const data = res.result.data?.data || res.result.data
        setCompany(data)
      } else {
        setError(res.result?.data?.message || res.result?.data?.data || 'Company not found')
      }
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Network error')
    }
  }

  useEffect(() => {
    fetchCompany()
  }, [companyId])

  const handleUpdateStatus = async (newStatus) => {
    setActioning(true)
    const url = `${getAPIBaseURL()}/employme/admin/companies/${companyId}/status`
    const headers = { 'Content-Type': 'application/json' }
    const res = await patchData(url, headers, { status: newStatus })
    setActioning(false)
    if (res.error === '' && res.result?.status === 200) {
      setCompany((prev) => ({ ...prev, status: newStatus }))
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to update status')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso || '—'
    }
  }

  const ownerName = () => {
    const o = company?.ownerUserId
    if (!o) return '—'
    return `${o.firstName || ''} ${o.lastName || ''}`.trim() || o.email || '—'
  }

  const locationStr = () => {
    const l = company?.location
    if (!l) return '—'
    const parts = [l.city, l.state, l.country].filter(Boolean)
    return parts.length ? parts.join(', ') : (l.addressLine || '—')
  }

  if (loading) {
    return (
      <section className="mx-auto max-w-4xl">
        <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
      </section>
    )
  }

  if (error || !company) {
    return (
      <section className="mx-auto max-w-4xl">
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error || 'Company not found'}
        </div>
        <Link
          to="/admin/app/employme/companies"
          className="inline-block rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to Companies
        </Link>
      </section>
    )
  }

  return (
    <section className="mx-auto max-w-4xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Link
          to="/admin/app/employme/companies"
          className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          ← Back to Companies
        </Link>
        <div className="flex gap-2">
          {company.status === 'PENDING_VERIFICATION' && (
            <button
              onClick={() => handleUpdateStatus('ACTIVE')}
              disabled={actioning}
              className="rounded-lg border border-green-600 px-4 py-2 text-sm font-medium text-green-600 hover:bg-green-50 disabled:opacity-50"
            >
              Verify
            </button>
          )}
          {company.status === 'ACTIVE' && (
            <button
              onClick={() => handleUpdateStatus('SUSPENDED')}
              disabled={actioning}
              className="rounded-lg border border-red-600 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              Suspend
            </button>
          )}
          {company.status === 'SUSPENDED' && (
            <button
              onClick={() => handleUpdateStatus('ACTIVE')}
              disabled={actioning}
              className="rounded-lg border border-green-600 px-4 py-2 text-sm font-medium text-green-600 hover:bg-green-50 disabled:opacity-50"
            >
              Activate
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-xl font-bold text-gray-900">{company.name}</h1>
          <span
            className={`mt-2 inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${
              company.status === 'ACTIVE'
                ? 'bg-green-100 text-green-700'
                : company.status === 'PENDING_VERIFICATION'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {company.status}
          </span>
        </div>
        <div className="divide-y divide-gray-100 px-6">
          <InfoRow label="Industry" value={company.industry} />
          <InfoRow label="Size" value={company.size} />
          <InfoRow label="Location" value={locationStr()} />
          <InfoRow label="Contact Person" value={company.contactPersonName} />
          <InfoRow label="Contact Role" value={company.contactPersonRole} />
          <InfoRow label="Contact Email" value={company.contactEmail} />
          <InfoRow label="Contact Phone" value={company.contactPhone} />
          <InfoRow label="Owner" value={ownerName()} />
          <InfoRow label="Owner Email" value={company.ownerUserId?.email} />
          <InfoRow label="Jobs Posted" value={company.jobCount ?? 0} />
          <InfoRow label="Created" value={formatDate(company.createdAt)} />
          <InfoRow label="Last Updated" value={formatDate(company.updatedAt)} />
        </div>
      </div>
    </section>
  )
}

export default AdminCompanyDetails
